import { type File } from '@kalos/kalos-rpc';
import { Button, Card, CardFooter, CardHeader, CardTitle, LoadingIcon } from '@kalos/ui';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from '@radix-ui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';

import { useS3FileUrlQuery } from '../../../../hooks/react-query/useS3';
import {
  convertHeicToJpeg,
  getEnhancedMimeType,
  getMimeType,
  S3ClientService,
} from '../../../../tools/helpers';
import { RotateZoomImage } from '../../../AltGallery/main';

// ---------------------------------------------------------------------------
// Module-level cache so processed images (e.g., converted HEIC → JPEG blobs)
// persist even if the component unmounts/remounts. Keyed by bucket/filename (stable).
// ---------------------------------------------------------------------------
interface CachedImageEntry {
  state: 'pending' | 'ready' | 'failed';
  blob: Blob | null; // null = trusted original (no conversion required)
  url: string | null; // object URL for blob or null if direct S3 preview is fine
}

const imageProcessCache: Record<string, CachedImageEntry> = {};

type ImageGalleryProps<T extends Pick<File, 'id' | 'name' | 'bucket' | 'mimeType'>> = {
  title?: string;
  files: T[];
  /** Which file to show first (e.g. when opening from a thumbnail grid). Defaults to 0. */
  initialActiveIndex?: number;
  actions?: (props: { original: T }) => React.ReactNode;
  renderOverlay?: (file: T) => React.ReactNode;
  renderHeader?: (file: T) => React.ReactNode;
};

export const ImageGallery = <T extends Pick<File, 'id' | 'name' | 'bucket' | 'mimeType'>>({
  title,
  files,
  initialActiveIndex = 0,
  actions,
  renderOverlay,
  renderHeader,
}: ImageGalleryProps<T>) => {
  // Reference the shared cache so we can mutate without triggering renders.
  const processedCache = React.useRef(imageProcessCache);
  const [activeImageIdx, setActiveImageIdx] = useState(() =>
    Math.min(initialActiveIndex, Math.max(0, files.length - 1)),
  );
  const [convertedImageUrl, setConvertedImageUrl] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // ----------------------------------------------------------------------------------
  // Build a stable cache key **once** per render so we can use it both for the cache
  // check and for gating the S3 query. (We can't compute it after the hook call.)
  // ----------------------------------------------------------------------------------
  const currentFile = useMemo(() => files[activeImageIdx], [files, activeImageIdx]);

  const cacheKey = useMemo(() => {
    if (!currentFile) return '';
    return `${currentFile.bucket}/${currentFile.name}`;
  }, [currentFile]);

  const cacheEntry = cacheKey ? processedCache.current[cacheKey] : undefined;
  const isCachedReady = cacheEntry?.state === 'ready';
  const isTracked = !!cacheEntry; // includes pending/failed

  useEffect(() => {
    const maxIdx = Math.max(0, files.length - 1);
    const idx = Math.min(Math.max(0, initialActiveIndex), maxIdx);
    setActiveImageIdx(idx);
    setConvertedImageUrl(null);
  }, [files, files.length, initialActiveIndex]);

  // Only skip the network call if we already have either a blob URL or an existing
  // cached URL. If the cache entry has no URL (trusted original) we still need to
  // fetch the signed S3 URL.
  const skipNetwork = isCachedReady && (cacheEntry?.url !== null || cacheEntry?.blob !== null);

  const fetchImageUrl = useS3FileUrlQuery({
    filter: {
      fileName: currentFile?.name ?? '',
      bucket: currentFile?.bucket ?? '',
    },
    // If we already have a cached conversion/URL, skip the network call
    enabled: files.length > 0 && !!currentFile && !skipNetwork,
  });

  const { mimeType } = useMemo(
    () => ({
      mimeType:
        files.at(activeImageIdx)?.mimeType != ''
          ? files.at(activeImageIdx)?.mimeType
          : getMimeType(files?.at(activeImageIdx)?.name ?? ''),
    }),
    [activeImageIdx, files],
  );

  // Process file data - handle PDFs and potential HEIC conversions only when needed
  useEffect(() => {
    let currentUrl: string | null = null;

    if (!currentFile) return;

    // If we already processed this file, reuse it
    if (isCachedReady && cacheEntry) {
      console.debug('[ImageGallery] Cache HIT', cacheKey, { cacheEntry });
      const { url: cachedUrl } = cacheEntry;
      setConvertedImageUrl(cachedUrl);
      setIsConverting(false);
      return;
    }

    // Someone else already started processing; don't duplicate work
    if (isTracked && cacheEntry?.state === 'pending') {
      console.debug('[ImageGallery] Cache state pending, skip processing', cacheKey);
      return;
    }

    console.debug('[ImageGallery] Cache MISS', cacheKey, {
      hasExtension: currentFile.name.includes('.') && !!currentFile.name.split('.').pop(),
      mimeType: currentFile.mimeType,
    });

    if (!fetchImageUrl.data) return;

    // Mark as pending before starting processing (after we have the data we need)
    processedCache.current[cacheKey] = { state: 'pending', blob: null, url: null };

    const fileName = currentFile.name;
    const fileMimeType = currentFile.mimeType || getMimeType(fileName);

    const hasExtension = fileName.includes('.') && !!fileName.split('.').pop();
    const isPdf = fileMimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

    const hasMime = fileMimeType !== '';

    // PDFs must always be converted to blob URLs to prevent auto-downloading
    const needsInspection = isPdf || !(hasExtension && hasMime);

    if (!needsInspection) {
      // File has trustworthy extension & mime type → no extra work.
      processedCache.current[cacheKey] = { state: 'ready', blob: null, url: null };
      console.debug('[ImageGallery] Marked trusted in cache', cacheKey);
      setConvertedImageUrl(null);
      return;
    }

    const processFile = async () => {
      setIsConverting(true);
      try {
        const response = await fetch(fetchImageUrl.data);
        if (!response.ok) {
          console.error('Failed to fetch file:', response.statusText);
          return;
        }

        const arrayBuffer = await response.arrayBuffer();

        let blob: Blob | undefined;

        if (isPdf) {
          blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        }

        if (!isPdf) {
          // Use enhanced detection to confirm type
          const enhancedMimeType = getEnhancedMimeType(fileName, new Uint8Array(arrayBuffer));

          if (enhancedMimeType === 'image/heic' || enhancedMimeType === 'image/heif') {
            blob = await convertHeicToJpeg(arrayBuffer, fileName, enhancedMimeType);
          } else {
            // No conversion needed; just use data as-is (skip creating blob, preview via original URL)
            setConvertedImageUrl(null);
            return;
          }
        }

        if (blob) {
          currentUrl = URL.createObjectURL(blob);
          // cache the result
          processedCache.current[cacheKey] = { state: 'ready', blob, url: currentUrl };
          console.debug('[ImageGallery] Stored converted URL in cache', cacheKey);
          setConvertedImageUrl(currentUrl);
        } else {
          // trusted original (no blob)
          processedCache.current[cacheKey] = { state: 'ready', blob: null, url: null };
        }
      } catch (error) {
        console.error('Error processing file:', error);
        // Record a null entry so we don't retry endlessly on failures (e.g., CORS)
        processedCache.current[cacheKey] = { state: 'failed', blob: null, url: null };
        setConvertedImageUrl(null);
      } finally {
        setIsConverting(false);
      }
    };

    processFile();

    return () => {
      // Do not revoke cached URLs to keep them alive for future views
    };
  }, [fetchImageUrl.data, currentFile, isCachedReady, isTracked, cacheKey, cacheEntry]);

  const downloadFile = async () => {
    setIsDownloading(true);
    try {
      const currentFile = files[activeImageIdx];
      const cached = processedCache.current[cacheKey];

      // If we already have a cached blob (converted), use it directly for download
      if (cached?.state === 'ready' && cached.blob) {
        const downloadName = currentFile.name.replace(/\.(heic|heif)$/i, '.jpg');
        const blobUrl = cached.url ?? URL.createObjectURL(cached.blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        return;
      }

      // Get S3 download URL
      const url = await S3ClientService.getFileS3BucketUrl(currentFile.name, currentFile.bucket);
      if (!url) {
        console.error('Failed to get download URL');
        return;
      }

      // Get base filename
      const baseName = currentFile.name.includes('.')
        ? currentFile.name.substring(0, currentFile.name.lastIndexOf('.'))
        : currentFile.name;

      // Fetch the file first to detect actual content type
      const response = await fetch(url);
      const fileData = await response.arrayBuffer();
      const uint8Array = new Uint8Array(fileData);

      // Use enhanced MIME type detection with the actual file data
      const enhancedMimeType = getEnhancedMimeType(currentFile.name, uint8Array);
      const isHeicByContent =
        enhancedMimeType === 'image/heic' || enhancedMimeType === 'image/heif';
      const isHeicByName =
        currentFile.name.toLowerCase().endsWith('.heic') ||
        currentFile.mimeType?.toLowerCase().includes('image/heic');
      const isHeic = isHeicByName || isHeicByContent;

      // Determine download filename and mime type
      let downloadName = currentFile.name;
      let downloadMimeType = enhancedMimeType;

      if (downloadMimeType && downloadMimeType.includes('/')) {
        let extension = downloadMimeType.split('/')[1].replace('jpeg', 'jpg');

        // For HEIC files, always convert to JPG for compatibility
        if (isHeic) {
          extension = 'jpg';
          downloadMimeType = 'image/jpeg';
        }

        downloadName = `${baseName}.${extension}`;
      }

      let blob;
      // Convert HEIC to JPEG if needed
      if (isHeic) {
        blob = await convertHeicToJpeg(fileData, currentFile.name, enhancedMimeType);
      } else {
        blob = new Blob([fileData], { type: downloadMimeType });
      }

      // Create download link
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const displayUrl = convertedImageUrl || cacheEntry?.url || fetchImageUrl.data || undefined;

  return (
    <Card className="image-gallery-container relative flex h-full w-full flex-1 flex-col overflow-hidden">
      {!!title && (
        <CardHeader className="flex-shrink-0 bg-white">
          <div>
            <CardTitle className="text-base font-normal">{title}</CardTitle>
          </div>
        </CardHeader>
      )}

      {/* Custom header section for current file */}
      {renderHeader && currentFile && (
        <div className="flex-shrink-0">{renderHeader(currentFile)}</div>
      )}

      <div
        className="image-gallery-protection flex w-full flex-1 items-center justify-center overflow-hidden"
        style={{ touchAction: 'none' }}
      >
        {fetchImageUrl.isLoading || isConverting ? (
          <div className="flex h-full w-full items-center justify-center">
            <LoadingIcon className="text-muted-foreground h-8 w-8 animate-spin" />
          </div>
        ) : files?.length ? (
          mimeType === 'application/pdf' ? (
            <div className="h-full w-full">
              {convertedImageUrl ? (
                // Use blob URL for PDFs to prevent auto-download
                <iframe
                  title="pdf-preview"
                  src={convertedImageUrl}
                  className="h-full w-full"
                  style={{ border: 'none' }}
                />
              ) : (
                // Wait for blob conversion before showing PDF
                <div className="flex h-full w-full items-center justify-center">
                  <LoadingIcon className="text-muted-foreground h-8 w-8 animate-spin" />
                </div>
              )}
            </div>
          ) : displayUrl ? (
            <div
              className="relative h-full w-full overflow-hidden"
              style={{
                touchAction: 'none',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            >
              <div
                className="h-full w-full"
                style={{
                  touchAction: 'none',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                }}
              >
                <RotateZoomImage url={displayUrl} fullScreenEnabled disableTransform={true} />
              </div>
              {/* Custom overlay if provided (rendered on top of photo) */}
              {renderOverlay && currentFile && renderOverlay(currentFile)}
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <LoadingIcon className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          )
        ) : (
          <div className="absolute flex h-full w-full items-center justify-center">
            <span className="text-xl">No files found.</span>
          </div>
        )}
      </div>

      <CardFooter className="flex flex-col items-center gap-2 p-2">
        <div className="flex items-center gap-2">
          {fetchImageUrl.isSuccess && actions?.({ original: files[activeImageIdx] })}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={downloadFile}
            isLoading={isDownloading}
            disabled={fetchImageUrl.isFetching || !fetchImageUrl.data || isDownloading}
            className="flex items-center gap-1"
          >
            <DownloadIcon className="h-4 w-4" />
            Download
          </Button>
          {files.length > 1 && (
            <>
              <button
                className="rounded-full bg-gray-100 p-1 hover:bg-gray-200 disabled:opacity-50"
                onClick={() =>
                  setActiveImageIdx((prev) => (prev > 0 ? prev - 1 : files.length - 1))
                }
                disabled={fetchImageUrl.isLoading || isConverting}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-muted-foreground text-xs">
                {activeImageIdx + 1} / {files.length}
              </span>
              <button
                className="rounded-full bg-gray-100 p-1 hover:bg-gray-200 disabled:opacity-50"
                onClick={() =>
                  setActiveImageIdx((prev) => (prev < files.length - 1 ? prev + 1 : 0))
                }
                disabled={fetchImageUrl.isLoading || isConverting}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
